import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {WidgetData} from '../../../../../../../../core/shared/widget/widget-data';
import {WidgetFactory} from '../../../../../../../../core/shared/widget/service/widget.factory';
import {FilterCriteria} from '../../../../../../../../core/shared/filter/api/filter.criteria';
import {ObjectIdentifier} from '../../../../../../../../core/shared/basic/object-identifier';
import {ObjectIdentifierData} from '../../../../../../../../core/shared/basic/object-identifier-data';
import {
    MvsObjectNavigationService
} from '../../../../../../../../core/shared/navigation/mvs-object-navigation.service';
import {
    MvsObjectNavigationEntry
} from '../../../../../../../../core/shared/navigation/mvs-object-navigation-entry';
import {
    MvsObjectNavigationActionEnum
} from '../../../../../../../../core/shared/navigation/mvs-object-navigation-action-enum';
import {buildCwCaseTypeExternalMessageMappingTableWidget} from '../../../../../../widget/cw-external-message-mapping.widget';
import {MvsCoreService} from '../../../../../../../../core/shared/service/mvs-core.service';
import {
    CW_SCOPE_COLUMN_LABEL,
    CW_SCOPE_CUSTOM_FIELD_ID,
    CW_SCOPE_INHERITANCE_HINT,
    CW_SCOPE_OPEN_CONFIG_LABEL,
    CW_SCOPE_PRECEDENCE_HINT,
    cwOpenPartyVersionConfig,
    cwScopedCreateCallback
} from '../../../../../../widget/cw-carrier-scope.util';
// [CW-VP-DIM] Vorbelegungen je Antragstyp (05-design-vp-dimension.md §4.1)
import {buildCwCaseTypeDefaultTableWidget} from '../../../../../../widget/cw-case-type-default.widget';
// [CW-TICKET-TYPES] Tickettypen je Vorgangstyp (Nachbearbeitung / Sonstiges)
import {buildCwCaseTypeTicketTypeWidget} from '../../../../../../widget/cw-case-type-ticket-type.widget';
import {cwPersistTabParam, cwReadTabParam, cwWidgetToolbarCreate} from '../../shared/cw-case-type-widget.util';
import {Sorting} from '../../../../../../../../core/shared/misc/sorting';
import {QlService} from '../../../../../../../../core/shared/ql/service/ql.service';
import {QlQueryResult} from '../../../../../../../../core/shared/ql/builder/ql-query-builder';
import {QlPipeResultDto} from '../../../../../../../../core/shared/ql/data/dto/ql-pipe-result.dto';
import {DtoDetail} from '../../../../../../../../core/shared/dto/dto.detail';
import {
    MvsFormControlOverwrite
} from '../../../../../../../../core/shared/logic/form/logic/mvs-form-control-overwrite';
import {
    MvsFormFieldAccessEnum
} from '../../../../../../../../core/shared/logic/form/logic/mvs-form-field-access.enum';
import {
    WidgetToolbarCallbackInterface
} from '../../../../../../../../core/shared/widget/widget-toolbar-callback.interface';
import {MvsMessageService} from '../../../../../../../../core/shared/service/message/mvs-message.service';

/**
 * [CW-STEP-ACTIONS] The orderPriority a new step action is created with. The column is NOT NULL, the
 * generic form has no default for it; a step with several actions runs them in ascending order, so the
 * configurator raises or lowers this value from a sensible start.
 */
const STEP_ACTION_DEFAULT_ORDER_PRIORITY = 10;

/** The object types whose ids narrow the create forms of this surface (FK {@code caseType}). */
const CASE_TYPE_STEP = 'cw.CwCaseTypeStep';
const CASE_TYPE_NOTIFICATION_TYPE = 'cw.CwCaseTypeNotificationType';

/**
 * The QL query name of each id load — ONE PER OBJECT TYPE, and that is load-bearing (review C1).
 *
 * The step-actions tab starts both loads in the same turn, so {@code QlService} sends them as ONE batched POST.
 * Its {@code normalizeBatch} renames a query whose name another request of the batch already uses and hands that
 * request its answer under the RENAMED key. With the one shared name {@code 'ids'} the second list was therefore
 * read as missing: under HEAD as "none" (no narrowing at all — a second road to F-1), after the first fix as
 * "failed". {@link idQueryPipe} additionally survives a rename forced by a FOREIGN request of the same name.
 */
export const CW_ID_QUERY_NAMES: Readonly<Record<string, string>> = {
    [CASE_TYPE_STEP]: 'caseTypeStepIds',
    [CASE_TYPE_NOTIFICATION_TYPE]: 'caseTypeNotificationTypeIds',
};

/** The German plural each object type is named by in the toasts. */
const ID_LIST_SUBJECTS: Readonly<Record<string, string>> = {
    [CASE_TYPE_STEP]: 'Schritte',
    [CASE_TYPE_NOTIFICATION_TYPE]: 'Benachrichtigungen',
};

/** [CW-STEP-ACTIONS] Tab hint when this case type has no notification type yet — the dropdown offers nothing. */
export const CW_STEP_ACTION_NO_NOTIFICATION_TYPE_HINT =
    'Für diesen Vorgangstyp ist noch keine Benachrichtigung angelegt. Bitte zuerst unter „Übersicht“ → '
    + '„Benachrichtigungen“ anlegen.';

/** [CW-STEP-ACTIONS] Tab hint when this case type has no step yet — the dropdown offers nothing. */
export const CW_STEP_ACTION_NO_STEP_HINT =
    'Für diesen Vorgangstyp sind noch keine Schritte angelegt. Bitte zuerst unter „Modellierung“ mit '
    + '„Schritt hinzufügen“ anlegen.';

/** [CW-STEP-ACTIONS] Tab hint when an allow-list could not be read — „Anlegen" is refused until it is. */
export const CW_STEP_ACTION_ID_LOAD_FAILED_HINT =
    'Die Schritte bzw. Benachrichtigungen dieses Vorgangstyps konnten nicht geladen werden. Bis sie geladen sind, '
    + 'können keine Schritt-Aktionen angelegt und Schritt bzw. Benachrichtigung bestehender nicht geändert werden.';

/** Placeholder of a dropdown narrowed to nothing because this case type has no notification type. */
export const CW_NO_NOTIFICATION_TYPE_PLACEHOLDER = 'Keine Benachrichtigung für diesen Vorgangstyp angelegt';

/** Placeholder of a dropdown narrowed to nothing because this case type has no step. */
export const CW_NO_STEP_PLACEHOLDER = 'Keine Schritte für diesen Vorgangstyp angelegt';

/**
 * Toast title and texts for a form opened while an allow-list is NOT KNOWN. Worded for BOTH ways in: a create is
 * refused, an edit opens with the field read-only ({@code narrowToCaseType}) — "gesperrt" and "keine Neuanlage" hold
 * for either. They name only the lists the form actually waits for (review L2: „Externe Operationen" waits for the
 * steps alone).
 */
export const CW_ID_LIST_TOAST_TITLE = 'Auswahl gesperrt';

export function cwIdListPendingText(objectTypes: string[]): string {
    return `Die ${idListSubject(objectTypes)} dieses Vorgangstyps werden noch geladen. Bis dahin ist die Auswahl `
        + 'gesperrt und eine Neuanlage nicht möglich.';
}

export function cwIdListFailedText(objectTypes: string[]): string {
    return `Die ${idListSubject(objectTypes)} dieses Vorgangstyps konnten nicht geladen werden. Die Auswahl bleibt `
        + 'gesperrt, eine Neuanlage ist nicht möglich; das Laden wird erneut versucht.';
}

function idListSubject(objectTypes: string[]): string {
    return objectTypes.map(objectType => ID_LIST_SUBJECTS[objectType]).join(' und ');
}

/**
 * The answer of ONE id query, read robustly: under its own name first, else the request's SOLE pipe.
 *
 * {@code QlService.normalizeBatch} renames a query whose name a FOREIGN request of the same 50 ms batch already
 * uses, and returns the answer under the renamed key (a platform defect, reported separately). {@code mapResults}
 * hands each request its OWN pipes only, so for a single-query request the sole pipe is its answer, whatever it is
 * called. {@code null} when there is no pipe at all.
 */
function idQueryPipe(result: QlQueryResult, queryName: string): QlPipeResultDto | null {
    const own = result?.getPipeResult(queryName);
    if (own) {
        return own;
    }
    const pipes = Object.values(result?.getRawResponse()?.pipeData ?? {}) as QlPipeResultDto[];
    return pipes.length === 1 ? pipes[0] : null;
}

/**
 * Whether the id query's pipe really ANSWERED. A per-pipe QL failure comes back as HTTP 200 with no rows —
 * read as an answer, it would claim "this case type has none". A missing pipe, or one carrying an error
 * message (severity 2, the same test the QL builder's own error toast applies), is a failure.
 */
function isAnsweredPipe(pipe: QlPipeResultDto | null): boolean {
    return pipe != null && !(pipe.messages ?? []).some(message => message?.severity === 2);
}

/**
 * AI:
 * Status: "in progress"
 * Type: Component
 * SubType: CrudIntegration
 * Reason: "Weitere" navigation point of the CwCaseType standard view — the catch-all tabs for
 *   config not covered by the other navigation points (external operations + their step buttons,
 *   [CW-STEP-ACTIONS] Schritt-Aktionen, message mapping, application templates, text blocks,
 *   [CW-VP-DIM] Vorbelegungen, [CW-TAA-V3] Unterschriftsrollen, [CW-TICKET-TYPES] Tickettypen).
 *   Inactive tabs are not rendered (@if lazy-init).
 */
@Component({
    selector: 'cw-case-type-others',
    templateUrl: './cw-case-type-others.component.html',
    styleUrls: ['./cw-case-type-others.component.scss'],
    standalone: false
})
export class CwCaseTypeOthersComponent implements OnInit, OnChanges {

    @Input() caseTypeId: number;

    activeTab: string = 'externalOperations';

    private readonly validTabs = [
        'externalOperations', 'stepActions', 'messageMapping', 'applicationTemplates', 'textBlocks',
        'caseTypeDefaults', 'signatureRoleSources', 'ticketTypes'
    ];

    externalOperationWidget: WidgetData;
    /**
     * [CW-TAA-V3] The step buttons of the SELECTED external operation
     * ({@code cw.CwCaseTypeExternalOperationStep}, FK {@code externalOperation}). The n:m surface
     * next to the 1:1 {@code stepBinding}: one operation may appear as a button on several steps,
     * which is why this hangs off the operation drilldown rather than off a tab of its own.
     */
    externalOperationStepWidget: WidgetData;

    /** Id of the row currently selected in the operations table — the axis of the step table. */
    selectedExternalOperationId: number | null = null;

    /**
     * The ids of every {@code cw.CwCaseTypeStep} of THIS case type — the allow-list that narrows the
     * {@code caseTypeStepDtoId} offer of the step-button and the step-action create forms.
     *
     * {@code CwCaseTypeExternalOperationStep} carries two mandatory foreign keys. The operation is
     * known from the selection and is preset; the step is the configurator's choice, and without a
     * narrowing the generic form offers EVERY step of the tenant, steps of foreign case types
     * included. Such a row is wrong by construction, and nothing downstream would catch it.
     *
     * Two states that must never be confused (F-1, clone/rathje):
     * <ul>
     *   <li>{@code null} — NOT KNOWN: the load is still in flight or it failed. The create callbacks
     *       refuse to open the form ({@link refuseWhileIdListsUnknown}); an unfiltered offer is never
     *       the fallback.</li>
     *   <li>{@code []} — KNOWN, and this case type has none. The dropdown is narrowed to nothing.</li>
     * </ul>
     *
     * Loaded once per case type when a tab that needs it is opened, and read at click time by the
     * create callbacks.
     */
    private caseTypeStepIds: number[] | null = null;

    /**
     * [CW-STEP-ACTIONS] The step actions of this case type ({@code cw.CwCaseTypeStepAction}) — what the
     * step engine additionally does when a step is completed (V1: send a notification). Flat on the case
     * type: the action has no configurable children, its executions ({@code cw.CwCaseStepActionRun}) are
     * runtime rows.
     */
    stepActionWidget: WidgetData;

    /**
     * [CW-STEP-ACTIONS] The ids of every {@code cw.CwCaseTypeNotificationType} of THIS case type — the
     * allow-list that narrows the {@code caseTypeNotificationTypeDtoId} offer of the step-action create
     * form, for the same reason and with the same two states as {@link caseTypeStepIds}: a notification
     * type of a foreign case type is never sent by this case type's engine (CWCHK-B11).
     *
     * The {@code []} state is the one F-1 was about: a case type with no notification type of its own
     * used to read as "do not narrow" and offered every notification type of the tenant.
     */
    private caseTypeNotificationTypeIds: number[] | null = null;

    /**
     * The id loads currently in flight, keyed {@code objectType#caseTypeId}. {@link buildActiveTab} runs
     * twice on init (directly, and again once the party-version lookup answers), and without this the
     * second run would fire every id load a second time while the first is still on its way.
     */
    private readonly idLoadsInFlight = new Set<string>();

    /**
     * The object types whose id load FAILED for the current case type. Drives the tab's error hint and
     * the refusal toast; cleared per object type by the next successful load, and entirely when the case
     * type changes.
     */
    private readonly failedIdLoads = new Set<string>();

    /** [CW-STEP-ACTIONS] German texts of the „Schritt-Aktionen" tab hints. */
    readonly noNotificationTypeHint = CW_STEP_ACTION_NO_NOTIFICATION_TYPE_HINT;
    readonly noStepHint = CW_STEP_ACTION_NO_STEP_HINT;
    readonly idLoadFailedHint = CW_STEP_ACTION_ID_LOAD_FAILED_HINT;

    /**
     * [CW-TAA-V3] The signature role sources of this case type
     * ({@code cw.CwCaseTypeSignatureRoleSource}) — flat, filtered on the case type.
     */
    signatureRoleSourceWidget: WidgetData;

    /**
     * [CW-TICKET-TYPES] The ticket types of this case type ({@code cw.CwCaseTypeTicketType}) with their
     * internal type — flat, filtered on the case type.
     */
    ticketTypeWidget: WidgetData;

    externalMessageMappingWidget: WidgetData;
    applicationTemplateWidget: WidgetData;
    textBlockWidget: WidgetData;
    /** [CW-VP-DIM] Vorbelegungen (Vorschläge) dieses Antragstyps — Einstieg in die Objektseite. */
    caseTypeDefaultWidget: WidgetData;

    /**
     * #C.1 (cw_sales_enhancements_round_2) — the cc.ExternalPartyVersion this case type is bound to,
     * i.e. the version stream whose Meldungs-Mapping rows it INHERITS. {@code null} for a case type
     * with no version binding; the tab then shows its own rows only, exactly as before.
     */
    caseTypeExternalPartyVersionId: number | null = null;

    /**
     * Whether {@link caseTypeExternalPartyVersionId} has been resolved yet. The Meldungs-Mapping
     * widget is built ONLY after this is true, because the union filter has to be part of the very
     * first query — rebuilding it afterwards would re-issue the query and discard the table state.
     */
    partyVersionResolved: boolean = false;

    /** German hint shown above the Meldungs-Mapping table — what an inherited row is, where it is edited. */
    readonly scopeInheritanceHint = CW_SCOPE_INHERITANCE_HINT;

    /** German statement of the backend resolution precedence (4·idExact + 2·opExact + 1·caseTypeScoped). */
    readonly scopePrecedenceHint = CW_SCOPE_PRECEDENCE_HINT;

    /** Field id of the Geltungsbereich badge column — the ng-template must carry exactly this id. */
    readonly scopeCustomFieldId = CW_SCOPE_CUSTOM_FIELD_ID;

    /** German header of that column — ONE spelling, shared with the curated column set. */
    readonly scopeColumnLabel = CW_SCOPE_COLUMN_LABEL;
    /** German label of the button that opens the party-version configuration surface. */
    readonly openPartyVersionConfigLabel = CW_SCOPE_OPEN_CONFIG_LABEL;


    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private navigationService: MvsObjectNavigationService,
        private coreService: MvsCoreService,
        private qlService: QlService,
        private messageService: MvsMessageService) {
    }

    ngOnInit(): void {
        this.activeTab = cwReadTabParam(this.route, this.validTabs, this.activeTab);
        this.loadCaseTypeExternalPartyVersion();
        this.buildActiveTab();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['caseTypeId'] && !changes['caseTypeId'].firstChange) {
            this.externalOperationWidget = null;
            // The selection belongs to the PREVIOUS case type and has to fall with it — otherwise the
            // step table keeps showing the buttons of an operation that is no longer on screen. The
            // step allow-list is case-type scoped for the same reason and is reloaded below.
            this.selectedExternalOperationId = null;
            this.externalOperationStepWidget = null;
            this.caseTypeStepIds = null;
            this.stepActionWidget = null;
            this.caseTypeNotificationTypeIds = null;
            this.failedIdLoads.clear();
            this.externalMessageMappingWidget = null;
            this.applicationTemplateWidget = null;
            this.textBlockWidget = null;
            this.caseTypeDefaultWidget = null;
            this.signatureRoleSourceWidget = null;
            this.ticketTypeWidget = null;
            this.loadCaseTypeExternalPartyVersion();
            this.buildActiveTab();
        }
    }

    onTabChange(tab: string | number): void {
        this.activeTab = String(tab);
        cwPersistTabParam(this.router, this.route, this.activeTab);
        this.buildActiveTab();
    }

    private buildActiveTab(): void {
        switch (this.activeTab) {
            case 'externalOperations':
                if (!this.externalOperationWidget) {
                    /**
                     * AI: Status "in progress" | Type Widget | SubType Runtime
                     * Reason: CwCaseTypeExternalOperation table scoped to the case type (Weitere).
                     */
                    this.externalOperationWidget = this.caseTypeTable(
                        'cw.CwCaseTypeExternalOperation.widget.basic.new',
                        'Externe Operationen',
                        'cw.CwCaseTypeExternalOperation',
                        'Keine externen Operationen vorhanden'
                    );
                }
                // The allow-list for the step drilldown's second mandatory FK. Loaded with the tab and
                // not with the selection, so it is already there by the time a row is clicked.
                this.loadCaseTypeStepIds();
                break;
            case 'stepActions':
                if (!this.stepActionWidget) {
                    /**
                     * AI: Status "in progress" | Type Widget | SubType Runtime
                     * Reason: [CW-STEP-ACTIONS] CwCaseTypeStepAction table scoped to the case type (Weitere).
                     */
                    // Sorted by orderPriority: several actions on the same step run in that order.
                    this.stepActionWidget = this.caseTypeTable(
                        'cw.CwCaseTypeStepAction.widget.basic.new',
                        'Schritt-Aktionen',
                        'cw.CwCaseTypeStepAction',
                        'Keine Schritt-Aktionen konfiguriert',
                        [new Sorting('e.orderPriority', true)]
                    );
                    // Replaces caseTypeTable's single-FK preset: the action needs the case type locked,
                    // orderPriority prefilled and its two further FKs narrowed to this case type.
                    this.stepActionWidget.functionCallbacks = this.buildStepActionCreateCallback();
                }
                // Both allow-lists of the create form, loaded with the tab — the callback reads them at
                // click time, so a load that lands after the widget was built is still picked up.
                this.loadCaseTypeStepIds();
                this.loadCaseTypeNotificationTypeIds();
                break;
            case 'messageMapping':
                // #C.1: built only once the version binding is known, so the very first query is
                // already the union (own rows + inherited rows). See partyVersionResolved.
                if (!this.externalMessageMappingWidget && this.partyVersionResolved) {
                    this.externalMessageMappingWidget = buildCwCaseTypeExternalMessageMappingTableWidget(
                        this.caseTypeId,
                        this.caseTypeExternalPartyVersionId ?? undefined
                    );
                    // #C.1: the table widget's „Anlegen" entry cannot be configured away, so it is
                    // PINNED to the case-type axis instead — a row created from a Vorgangstyp is a
                    // Vorgangstyp row, never a second copy on the version axis and never a scopeless
                    // row. Party-version rows are created on the party-version surface.
                    this.externalMessageMappingWidget.functionCallbacks =
                        cwScopedCreateCallback('caseType', this.caseTypeId);
                }
                break;
            case 'applicationTemplates':
                if (!this.applicationTemplateWidget) {
                    /**
                     * AI: Status "in progress" | Type Widget | SubType Runtime
                     * Reason: CwApplicationTemplate table scoped to the case type (Weitere).
                     */
                    this.applicationTemplateWidget = this.caseTypeTable(
                        'cw.CwApplicationTemplate.widget.basic.new',
                        'Antragstemplates',
                        'cw.CwApplicationTemplate',
                        'Keine Antragstemplates vorhanden'
                    );
                }
                break;
            case 'textBlocks':
                if (!this.textBlockWidget) {
                    /**
                     * AI: Status "in progress" | Type Widget | SubType Runtime
                     * Reason: CwTextBlock table scoped to the case type (Weitere).
                     */
                    this.textBlockWidget = this.caseTypeTable(
                        'cw.CwTextBlock.widget.basic.new',
                        'Textbausteine',
                        'cw.CwTextBlock',
                        'Keine Textbausteine vorhanden'
                    );
                }
                break;
            case 'caseTypeDefaults':
                if (!this.caseTypeDefaultWidget) {
                    // [CW-VP-DIM] Vorbelegungen dieses Antragstyps. Curated-Column-Widget aus dem
                    // gemeinsamen Builder (nicht caseTypeTable), weil der Vorschlag mehrere
                    // FK-Spalten auflöst (Kategorie, Produkt, Personenrolle).
                    this.caseTypeDefaultWidget = buildCwCaseTypeDefaultTableWidget(this.caseTypeId);
                    this.caseTypeDefaultWidget.functionCallbacks = cwWidgetToolbarCreate([
                        {field: 'caseTypeDtoId', value: this.caseTypeId}
                    ]);
                }
                break;
            case 'signatureRoleSources':
                if (!this.signatureRoleSourceWidget) {
                    /**
                     * AI: Status "in progress" | Type Widget | SubType Runtime
                     * Reason: CwCaseTypeSignatureRoleSource table scoped to the case type (Weitere) — the
                     *   per-role rules that say WHO signs. Flat on the case type: personType is an
                     *   OPTIONAL, secondary FK read only for source = Person, so it is a column of the
                     *   row and never a selection axis above it.
                     */
                    // Sorted by roleKey, then orderPriority: several rows may carry the SAME role and
                    // are walked as a CHAIN in orderPriority order, the first one that resolves to a
                    // person winning and the rest never being evaluated. Only this ordering puts a
                    // chain together on screen — any other sort scatters the rows of one role across
                    // the table and hides the fallback relationship that is the point of having them.
                    this.signatureRoleSourceWidget = this.caseTypeTable(
                        'cw.CwCaseTypeSignatureRoleSource.widget.basic.new',
                        'Unterschriftsrollen',
                        'cw.CwCaseTypeSignatureRoleSource',
                        'Keine Unterschriftsrollen konfiguriert',
                        [new Sorting('e.roleKey', true), new Sorting('e.orderPriority', true)]
                    );
                }
                break;
            case 'ticketTypes':
                if (!this.ticketTypeWidget) {
                    // [CW-TICKET-TYPES] Child table with the parent FK pre-set on create (CONFIG-012) —
                    // the shared builder, mirroring the Antragseingang's Tickettypen table.
                    this.ticketTypeWidget = buildCwCaseTypeTicketTypeWidget(this.caseTypeId);
                }
                break;
        }
    }

    /**
     * [CW-TAA-V3] Selecting an external operation → the step buttons of that operation.
     *
     * The rows of {@code cw.CwCaseTypeExternalOperationStep} hang off the OPERATION
     * ({@code externalOperation}), not off the case type, which is why this is a drilldown of the
     * selection rather than a „Weitere" tab of its own. The second mandatory FK
     * {@code caseTypeStep} stays the configurator's input — it is only NARROWED, to the steps of
     * this case type, see {@link buildExternalOperationStepCreateCallback}.
     */
    handleExternalOperationSelect(event: ObjectIdentifierData | ObjectIdentifier): void {
        const operationId = event?.objectId;
        if (!operationId) {
            return;
        }
        this.selectedExternalOperationId = operationId;
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: CwCaseTypeExternalOperationStep table (FK externalOperation) for the selected external
         *   operation — the n:m surface that exposes the operation as a button on further case type steps.
         */
        // The empty-state text carries the semantics: with no row at all the operation appears only
        // where its 1:1 step binding puts it, so an empty table is valid configuration, not a gap.
        this.externalOperationStepWidget = this.childTable(
            'cw.CwCaseTypeExternalOperationStep.widget.basic.new',
            'Schaltflächen auf Schritten',
            'cw.CwCaseTypeExternalOperationStep',
            'Keine zusätzlichen Schritte — die Operation erscheint nur auf ihrer Schrittbindung.',
            'externalOperation',
            operationId,
            new Sorting('e.orderPriority', true)
        );
        this.externalOperationStepWidget.functionCallbacks =
            this.buildExternalOperationStepCreateCallback(operationId);
    }

    /**
     * The create callback of the step-button table: preset the operation, and narrow the step offer
     * to the steps of THIS case type.
     *
     * Both mandatory foreign keys are handled here, by different means and for different reasons.
     * {@code externalOperationDtoId} is KNOWN from the selection, so it is written into the DTO and
     * locked to READ. {@code caseTypeStepDtoId} is a genuine choice, so it stays editable — but the
     * generic form would otherwise offer every {@code cw.CwCaseTypeStep} of the tenant, steps of
     * FOREIGN case types included, and a row wired that way is wrong by construction with nothing
     * downstream to catch it.
     *
     * The narrowing goes through the platform's own metadata channel,
     * {@code MvsFormControlOverwrite.addFieldValueListFilter}, which
     * {@code MvsFormControlService.applyFieldValueListFilter} applies to the field's value list —
     * the same channel the ci contract-person and cr address/contact surfaces use to restrict an
     * entity-reference offer, where the allowed keys are the referenced rows' ids.
     *
     * The allow-list is read at CLICK time, and its two states decide (see {@link caseTypeStepIds}): a
     * KNOWN list narrows — to nothing when this case type has no step, with a placeholder that says so
     * ({@link narrowToCaseType}) — and an UNKNOWN one refuses the form ({@link refuseWhileIdListsUnknown}).
     * The former "degrade to the unfiltered offer" is gone: it was the path through which F-1 offered the
     * rows of foreign case types. Enforcement stays with the backend; this is guidance, and guidance must
     * not mislead.
     */
    private buildExternalOperationStepCreateCallback(
        operationId: number): { onFunctionCreateNew: () => WidgetToolbarCallbackInterface } {

        return {
            onFunctionCreateNew: (): WidgetToolbarCallbackInterface => {
                const dto = new DtoDetail();
                dto['externalOperationDtoId'] = operationId;

                const formControlOverwrite = new MvsFormControlOverwrite();
                formControlOverwrite.addField('externalOperationDtoId', MvsFormFieldAccessEnum.READ);

                const cancel = this.refuseWhileIdListsUnknown([CASE_TYPE_STEP]);
                this.narrowToCaseType(
                    formControlOverwrite, 'caseTypeStepDtoId', this.caseTypeStepIds, CW_NO_STEP_PLACEHOLDER);

                return {dto: dto, formControlOverwrite: formControlOverwrite, cancel: cancel};
            }
        };
    }

    /**
     * [CW-STEP-ACTIONS] The create callback of the step-action table.
     *
     * <ul>
     *   <li>{@code caseTypeDtoId} is known and locked to READ (CONFIG-012).</li>
     *   <li>{@code orderPriority} is prefilled but stays editable: the backend rejects a row without it
     *       (EntityValidator rule ORDER_PRIORITY), and the generic form would otherwise open with the
     *       field empty.</li>
     *   <li>{@code caseTypeStepDtoId} and {@code caseTypeNotificationTypeDtoId} stay the configurator's
     *       choice, NARROWED to the rows of this case type through the same value-list filter and with
     *       the same two states as {@link buildExternalOperationStepCreateCallback}: a case type with no
     *       notification type offers NONE (F-1 — it used to offer every one of the tenant), and a list
     *       that is not known refuses the form.</li>
     * </ul>
     *
     * The allow-lists are read from the fields at CLICK time, not captured when the callback is built:
     * this widget is built in the same turn that starts both loads, so a captured value would always be
     * the "not known" of the moment before the answer.
     */
    private buildStepActionCreateCallback(): { onFunctionCreateNew: () => WidgetToolbarCallbackInterface } {
        return {
            onFunctionCreateNew: (): WidgetToolbarCallbackInterface => {
                const dto = new DtoDetail();
                dto['caseTypeDtoId'] = this.caseTypeId;
                dto['orderPriority'] = STEP_ACTION_DEFAULT_ORDER_PRIORITY;

                const formControlOverwrite = new MvsFormControlOverwrite();
                formControlOverwrite.addField('caseTypeDtoId', MvsFormFieldAccessEnum.READ);

                const cancel = this.refuseWhileIdListsUnknown([CASE_TYPE_STEP, CASE_TYPE_NOTIFICATION_TYPE]);
                this.narrowToCaseType(
                    formControlOverwrite, 'caseTypeStepDtoId', this.caseTypeStepIds, CW_NO_STEP_PLACEHOLDER);
                this.narrowToCaseType(
                    formControlOverwrite, 'caseTypeNotificationTypeDtoId', this.caseTypeNotificationTypeIds,
                    CW_NO_NOTIFICATION_TYPE_PLACEHOLDER);

                return {dto: dto, formControlOverwrite: formControlOverwrite, cancel: cancel};
            }
        };
    }

    /**
     * Restricts {@code fieldName} to the rows of this case type — ALWAYS, whatever state the list is in.
     *
     * <ul>
     *   <li>ids → exactly those.</li>
     *   <li>{@code []} → nothing, plus a placeholder naming why the dropdown is empty. The filter channel
     *       takes an empty list literally ({@code applyFieldValueListFilter} keeps no entry), and an empty,
     *       explained dropdown is the truth here: the case type has nothing that could be chosen.</li>
     *   <li>{@code null} (not known) → the field is READ-only. The create path never shows the form — it is
     *       refused — but the table widget's EDIT path ({@code mvs-widget-table.handleEdit}) reads this overwrite
     *       and ignores {@code cancel}, and the callback can neither tell the two paths apart nor see the row
     *       being edited, so it cannot narrow to the current value. READ is what keeps that edit honest (review
     *       M1): the stored value stays visible, nothing can be chosen, so no foreign row can be picked. A filter
     *       of {@code []} left „Bitte auswählen" over a field that holds a value.</li>
     * </ul>
     */
    private narrowToCaseType(overwrite: MvsFormControlOverwrite, fieldName: string, ids: number[] | null,
                             emptyPlaceholder: string): void {
        if (ids === null) {
            overwrite.addField(fieldName, MvsFormFieldAccessEnum.READ);
            return;
        }
        overwrite.addFieldValueListFilter(fieldName, ids);
        if (ids.length === 0) {
            overwrite.addFieldPlaceholder(fieldName, emptyPlaceholder);
        }
    }

    /**
     * Whether a create form must be refused because one of the allow-lists it narrows by is NOT KNOWN —
     * and, if so, says why in a toast and starts the missing loads again, so a later click can succeed.
     *
     * Two reasons, two texts: a load still in flight ("werden noch geladen") and a load that failed
     * ("konnten nicht geladen werden"), each naming only the lists this form waits for. Neither opens an
     * unrestricted form. The table's edit path runs this too and shows the same toast — the texts are worded
     * so that they hold there as well (the field is read-only, see {@link narrowToCaseType}).
     */
    private refuseWhileIdListsUnknown(objectTypes: string[]): boolean {
        const unknown = objectTypes.filter(objectType => this.idListOf(objectType) === null);
        if (unknown.length === 0) {
            return false;
        }
        if (unknown.some(objectType => this.failedIdLoads.has(objectType))) {
            this.messageService.showErrorMessage(CW_ID_LIST_TOAST_TITLE, cwIdListFailedText(unknown));
        } else {
            this.messageService.showInfoMessage(CW_ID_LIST_TOAST_TITLE, cwIdListPendingText(unknown));
        }
        unknown.forEach(objectType => this.loadIdList(objectType));
        return true;
    }

    /** [CW-STEP-ACTIONS] Known, and this case type has no notification type — the dropdown offers none. */
    get hasNoNotificationTypes(): boolean {
        return this.caseTypeNotificationTypeIds?.length === 0;
    }

    /** [CW-STEP-ACTIONS] Known, and this case type has no step — the dropdown offers none. */
    get hasNoSteps(): boolean {
        return this.caseTypeStepIds?.length === 0;
    }

    /** [CW-STEP-ACTIONS] An allow-list of the step-action form could not be read — „Anlegen" is refused. */
    get stepActionIdLoadFailed(): boolean {
        return this.failedIdLoads.has(CASE_TYPE_STEP) || this.failedIdLoads.has(CASE_TYPE_NOTIFICATION_TYPE);
    }

    /** [CW-STEP-ACTIONS] „Erneut laden" of the error hint: reads every allow-list that is not known yet. */
    reloadStepActionIdLists(): void {
        this.loadIdList(CASE_TYPE_STEP);
        this.loadIdList(CASE_TYPE_NOTIFICATION_TYPE);
    }

    /** The allow-list of {@code objectType} as it stands — {@code null}: not known. */
    private idListOf(objectType: string): number[] | null {
        return objectType === CASE_TYPE_STEP ? this.caseTypeStepIds : this.caseTypeNotificationTypeIds;
    }

    private loadIdList(objectType: string): void {
        if (objectType === CASE_TYPE_STEP) {
            this.loadCaseTypeStepIds();
        } else {
            this.loadCaseTypeNotificationTypeIds();
        }
    }

    /**
     * Loads the ids of every {@code cw.CwCaseTypeStep} of this case type into {@link caseTypeStepIds}.
     * A list that is already KNOWN — empty included — is not read again; a failed one stays {@code null}
     * and is read again by the next tab build, the next refused click or „Erneut laden".
     */
    private loadCaseTypeStepIds(): void {
        if (this.caseTypeStepIds !== null) {
            return;
        }
        this.loadIdsOfCaseType(CASE_TYPE_STEP, ids => {
            this.caseTypeStepIds = ids;
        });
    }

    /**
     * [CW-STEP-ACTIONS] Loads the ids of every {@code cw.CwCaseTypeNotificationType} of this case type
     * into {@link caseTypeNotificationTypeIds} — with the same two states as {@link loadCaseTypeStepIds}.
     */
    private loadCaseTypeNotificationTypeIds(): void {
        if (this.caseTypeNotificationTypeIds !== null) {
            return;
        }
        this.loadIdsOfCaseType(CASE_TYPE_NOTIFICATION_TYPE, ids => {
            this.caseTypeNotificationTypeIds = ids;
        });
    }

    /**
     * Lists the ids of every row of {@code objectType} that hangs off this case type (FK {@code caseType})
     * and hands them to {@code apply} — the empty list included, which is an ANSWER ("there are none").
     * A failure hands over nothing: {@code apply} is not called, the list stays {@code null} ("not known")
     * and {@code objectType} is recorded in {@link failedIdLoads}.
     *
     * One QL query that projects {@code id} only — the narrowing needs the ids and nothing else, so no
     * full DTO row is loaded. Its name is unique per object type ({@link CW_ID_QUERY_NAMES}): the loads of one
     * tab go out as ONE batched POST, and a shared name would be renamed by the batcher (review C1). Errors are
     * not shown as the QL builder's own toast ({@code showErrors(false)}): this is a background load, and the
     * refusal at click time says what went wrong in this surface's words. A per-pipe QL failure (an error
     * message on the pipe, or no pipe at all) answers HTTP 200 with no rows — it is detected here and counted
     * as a failure, never as "none".
     *
     * An answer that arrives after the case type changed is dropped: {@link ngOnChanges} has already
     * reset the list for the new case type, and writing the previous case type's ids into it would narrow
     * the new create form to rows it cannot use.
     */
    private loadIdsOfCaseType(objectType: string, apply: (ids: number[]) => void): void {
        const requestedCaseTypeId = this.caseTypeId;
        const queryName = CW_ID_QUERY_NAMES[objectType];
        const loadKey = `${objectType}#${requestedCaseTypeId}`;
        if (this.idLoadsInFlight.has(loadKey)) {
            return;
        }
        this.idLoadsInFlight.add(loadKey);
        // A new attempt is under way — the previous failure is no longer the state of this list.
        this.failedIdLoads.delete(objectType);

        this.qlService.builder()
            .showErrors(false)
            .query({
                name: queryName,
                type: 'view',
                start: {type: 'entity', name: objectType, as: 'e', fields: [{name: 'id'}]},
                filters: [FilterCriteria.create('e.caseTypeId', FilterCriteria.cOperatorEqual, requestedCaseTypeId)],
            })
            .execute()
            .subscribe({
                next: (result: QlQueryResult) => {
                    this.idLoadsInFlight.delete(loadKey);
                    if (requestedCaseTypeId !== this.caseTypeId) {
                        return;
                    }
                    const pipe = idQueryPipe(result, queryName);
                    if (!isAnsweredPipe(pipe)) {
                        this.failedIdLoads.add(objectType);
                        return;
                    }
                    // A QL row is a key/value map under the query's aliases ('e.id'); `unknown` keeps the
                    // type guard below the only way to get a number out.
                    const rows = (pipe.data ?? []) as Record<string, unknown>[];
                    apply(rows
                        .map(row => row?.['e.id'])
                        .filter((id): id is number => typeof id === 'number'));
                },
                error: () => {
                    this.idLoadsInFlight.delete(loadKey);
                    if (requestedCaseTypeId === this.caseTypeId) {
                        this.failedIdLoads.add(objectType);
                    }
                }
            });
    }

    /**
     * [CW-VP-DIM] Row click on the Vorbelegungen table → opens the DEDICATED Vorbelegung object page
     * (registered objectRoute {@code cw/cwCaseTypeDefault/:id}). A proposal owns child rows plus its
     * alias-keyed fieldValuesJson, so per PO decision Q7 it is never edited inline in this list.
     */
    handleCaseTypeDefaultSelect(event: ObjectIdentifierData | ObjectIdentifier): void {
        if (!event?.objectId) {
            return;
        }
        this.navigationService.navigateTo(
            MvsObjectNavigationEntry.createNavigationEntry(
                'cw.CwCaseTypeDefault', event.objectId, 'object', '', '', '',
                MvsObjectNavigationActionEnum.edit
            ),
            'main'
        );
    }

    /**
     * #C.1 (cw_sales_enhancements_round_2) — resolves the cc.ExternalPartyVersion of this case type,
     * the axis whose catalog rows the case type inherits. Best-effort: an unresolvable binding leaves
     * the id {@code null} and the tab shows the case type's own rows only — never an error.
     */
    private loadCaseTypeExternalPartyVersion(): void {
        this.partyVersionResolved = false;
        this.caseTypeExternalPartyVersionId = null;

        const service = this.coreService.getCrudService('cw.CwCaseType');
        if (!service) {
            this.partyVersionResolved = true;
            this.buildActiveTab();
            return;
        }

        service.get(this.caseTypeId).subscribe({
            next: (dto: any) => {
                this.caseTypeExternalPartyVersionId = dto?.externalPartyVersionDtoId ?? null;
                this.partyVersionResolved = true;
                this.buildActiveTab();
            },
            error: () => {
                this.partyVersionResolved = true;
                this.buildActiveTab();
            }
        });
    }

    /**
     * Opens Konfiguration → Partei-Version-Konfiguration WITH this case type's own version already
     * selected. Paired with the inheritance hint: naming a place the user then has to re-find, and
     * re-select in, is a hint that gets ignored. Takes the user OFF this page — deliberately, because
     * the row they want to change is not this case type's to change.
     */
    openPartyVersionConfig(): void {
        cwOpenPartyVersionConfig(this.router, this.caseTypeExternalPartyVersionId ?? undefined);
    }

    /** Whether the Meldungs-Mapping table can contain inherited rows at all (drives the hint panel). */
    hasInheritedScope(): boolean {
        return this.caseTypeExternalPartyVersionId != null;
    }

    /** Generic table-widget builder shared by every FK-`caseType`-scoped „Weitere" tab. */
    private caseTypeTable(alias: string, name: string, entity: string, noData: string,
                          sortings: Sorting[] = []): WidgetData {
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: Case-type-scoped list table (FK caseType) for a „Weitere" tab; the entity/alias are supplied by the calling tab builder.
         */
        const widget = WidgetFactory.createWidgetTableEntityQl(
            alias, name, entity, noData,
            [FilterCriteria.create('e.caseTypeId', FilterCriteria.cOperatorEqual, this.caseTypeId)],
            sortings,
            // resolveFkeyTexts: render foreign keys as their display text instead of the raw id.
            true
        );
        widget.functionCallbacks = cwWidgetToolbarCreate([{field: 'caseTypeDtoId', value: this.caseTypeId}]);
        return widget;
    }

    /**
     * Generic child TABLE builder (arbitrary FK relation) with a matching create preset — the
     * „Weitere" counterpart to {@code CwCaseTypeProductsComponent.childTable}, for rows that hang off
     * a SELECTED row of this surface rather than off the case type itself.
     *
     * The preset it installs covers a single foreign key. A caller that needs more than that — a
     * second mandatory FK to narrow, say — overwrites {@code functionCallbacks} afterwards; see
     * {@link buildExternalOperationStepCreateCallback}.
     */
    private childTable(alias: string, name: string, entity: string, noData: string,
                       fkRelation: string, parentId: number, sorting?: Sorting): WidgetData {
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: Child list table scoped to a selected parent row (FK <fkRelation>) of a „Weitere" tab; the entity/alias/relation are supplied by the calling selection handler.
         */
        const widget = WidgetFactory.createWidgetTableEntityQl(
            alias, name, entity, noData,
            [FilterCriteria.create(`e.${fkRelation}Id`, FilterCriteria.cOperatorEqual, parentId)],
            sorting ? [sorting] : [],
            // resolveFkeyTexts: render foreign keys as their display text instead of the raw id.
            true
        );
        // FK defaulting: without it a create would produce a row with no parent reference at all.
        widget.functionCallbacks = cwWidgetToolbarCreate([{field: `${fkRelation}DtoId`, value: parentId}]);
        return widget;
    }
}
